const ROOMS_MESSAGES = {
  en: {
    setup: {
      eyebrow: "Room counts",
      title: "How many rooms do you have?",
      copy:
        "Start with the quantity of rooms in each category. In the next step you will complete the details in a simple grid.",
      noCategoriesTitle: "Complete room categories first",
      noCategoriesCopy: "We need the room categories before preparing the room list.",
      countLabel: "Rooms in this category",
      totalLabel: "Total rooms",
      generate: "Create room list",
      reload: "Reload",
      createAndContinue: "Create and continue",
      saveAndContinue: "Save and continue",
      generateSuccess: "The room list is ready.",
      generateConfirmTrim: (count) =>
        `This will remove ${count} room slot${count === 1 ? "" : "s"} with information. Do you want to continue?`,
      loadError: "We could not load the saved room counts.",
      saveError: "We could not create the room list right now.",
      summaryTitle: "This first step is quick",
      summaryIntro:
        "Just tell us how many rooms there are in each category. We will prepare the editable grid for the next step.",
      summaryPoints: [
        "You only need quantities here.",
        "If you change the count later, we will add or remove room slots.",
        "The detailed data comes in the next screen.",
      ],
    },
    grid: {
      eyebrow: "Room details",
      title: "Complete your room details in a grid",
      copy:
        "Each row is one room. You can copy a room and paste its data into another one to move much faster.",
      backToSetup: "Back to room counts",
      save: "Save progress",
      reload: "Reload",
      copyHint: "Copied room",
      copyEmpty: "No room copied yet",
      roomNumber: "Room",
      floor: "Floor",
      sqMeters: "m²",
      numBeds: "Beds",
      bedType: "Bed type",
      view: "View",
      features: "Features",
      description: "Description",
      roomNumberPlaceholder: "201",
      bedTypePlaceholder: "Double bed",
      viewPlaceholder: "Sea view",
      featuresPlaceholder: "Balcony, sofa bed...",
      descriptionPlaceholder: "Optional note",
      copyRow: "Copy",
      pasteRow: "Paste",
      emptyTitle: "Create the room list first",
      emptyCopy: "Go to Room counts, set the quantities and create the room list before filling the grid.",
      clipboardCopied: "Room copied. You can paste it into another row.",
      clipboardEmpty: "Copy a room first.",
      clipboardPasted: "Data pasted into this row.",
      saveSuccess: "Your room grid has been saved.",
      loadError: "We could not load your saved rooms.",
      saveError: "We could not save the room grid right now.",
      validationDuplicate: (roomNumber) => `Room number ${roomNumber} is repeated.`,
      summaryTitle: "A practical way to fill this grid",
      summaryIntro: "Copy one complete row and paste it into similar rooms, then adjust only what changes.",
      summaryPoints: [
        "Use Room counts if you need to change the number of rooms.",
        "Copy from similar rooms to avoid writing the same data again and again.",
        "You can save at any time.",
      ],
      summaryTotalLabel: "Rooms in this step",
      summaryCategoriesLabel: "Categories with rooms",
      summarySavedLabel: "Last save",
      summaryNotSaved: "Not saved yet",
    },
  },
  es: {
    setup: {
      eyebrow: "Cantidad de habitaciones",
      title: "¿Cuántas habitaciones tienes?",
      copy:
        "Empieza indicando cuántas habitaciones hay en cada categoría. En el siguiente paso completarás los datos en una parrilla sencilla.",
      noCategoriesTitle: "Completa antes las categorías de habitaciones",
      noCategoriesCopy: "Necesitamos las categorías de habitaciones antes de preparar la lista.",
      countLabel: "Habitaciones de esta categoría",
      totalLabel: "Habitaciones totales",
      generate: "Crear lista de habitaciones",
      reload: "Recargar",
      createAndContinue: "Crear y continuar",
      saveAndContinue: "Guardar y continuar",
      generateSuccess: "La lista de habitaciones ya está preparada.",
      generateConfirmTrim: (count) =>
        `Esto eliminará ${count} hueco${count === 1 ? "" : "s"} con información. ¿Quieres continuar?`,
      loadError: "No hemos podido cargar las cantidades guardadas.",
      saveError: "No hemos podido crear la lista de habitaciones ahora mismo.",
      summaryTitle: "Este primer paso es rápido",
      summaryIntro:
        "Aquí solo necesitas indicar cantidades. Nosotros prepararemos la parrilla editable del siguiente paso.",
      summaryPoints: [
        "En esta pantalla solo se rellenan cantidades.",
        "Si luego cambias una cantidad, añadiremos o quitaremos huecos de habitaciones.",
        "Los datos detallados vienen en la siguiente pantalla.",
      ],
    },
    grid: {
      eyebrow: "Detalle de habitaciones",
      title: "Completa el detalle de tus habitaciones en una parrilla",
      copy:
        "Cada fila es una habitación. Puedes copiar una habitación y pegar sus datos en otra para avanzar mucho más rápido.",
      backToSetup: "Volver a cantidad de habitaciones",
      save: "Guardar progreso",
      reload: "Recargar",
      copyHint: "Habitación copiada",
      copyEmpty: "Todavía no has copiado ninguna habitación",
      roomNumber: "Habitación",
      floor: "Planta",
      sqMeters: "m²",
      numBeds: "Camas",
      bedType: "Tipo de cama",
      view: "Vista",
      features: "Características",
      description: "Descripción",
      roomNumberPlaceholder: "201",
      bedTypePlaceholder: "Cama doble",
      viewPlaceholder: "Vista al mar",
      featuresPlaceholder: "Balcón, sofá cama...",
      descriptionPlaceholder: "Nota opcional",
      copyRow: "Copiar",
      pasteRow: "Pegar",
      emptyTitle: "Primero crea la lista de habitaciones",
      emptyCopy: "Ve a Cantidad de habitaciones, indica las cantidades y crea la lista antes de rellenar la parrilla.",
      clipboardCopied: "Habitación copiada. Ya puedes pegarla en otra fila.",
      clipboardEmpty: "Primero copia una habitación.",
      clipboardPasted: "Los datos se han pegado en esta fila.",
      saveSuccess: "La parrilla de habitaciones se ha guardado correctamente.",
      loadError: "No hemos podido cargar tus habitaciones guardadas.",
      saveError: "No hemos podido guardar la parrilla de habitaciones ahora mismo.",
      validationDuplicate: (roomNumber) => `El número de habitación ${roomNumber} está repetido.`,
      summaryTitle: "Una forma práctica de rellenar esta parrilla",
      summaryIntro:
        "Copia una fila completa y pégala en habitaciones parecidas. Después ajusta solo lo que cambie.",
      summaryPoints: [
        "Usa Cantidad de habitaciones si necesitas cambiar el número de habitaciones.",
        "Copia desde habitaciones parecidas para no escribir lo mismo una y otra vez.",
        "Puedes guardar en cualquier momento.",
      ],
      summaryTotalLabel: "Habitaciones en este paso",
      summaryCategoriesLabel: "Categorías con habitaciones",
      summarySavedLabel: "Último guardado",
      summaryNotSaved: "Todavía sin guardar",
    },
  },
  ca: {
    setup: {
      eyebrow: "Quantitat d'habitacions",
      title: "Quantes habitacions tens?",
      copy:
        "Comença indicant quantes habitacions hi ha a cada categoria. En el pas següent completaràs les dades en una graella senzilla.",
      noCategoriesTitle: "Completa abans les categories d'habitacions",
      noCategoriesCopy: "Necessitem les categories d'habitacions abans de preparar la llista.",
      countLabel: "Habitacions d'aquesta categoria",
      totalLabel: "Habitacions totals",
      generate: "Crear llista d'habitacions",
      reload: "Recarregar",
      createAndContinue: "Crear i continuar",
      saveAndContinue: "Guardar i continuar",
      generateSuccess: "La llista d'habitacions ja està preparada.",
      generateConfirmTrim: (count) =>
        `Això eliminarà ${count} espai${count === 1 ? "" : "s"} amb informació. Vols continuar?`,
      loadError: "No hem pogut carregar les quantitats desades.",
      saveError: "No hem pogut crear la llista d'habitacions ara mateix.",
      summaryTitle: "Aquest primer pas és ràpid",
      summaryIntro:
        "Aquí només cal indicar quantitats. Nosaltres prepararem la graella editable del pas següent.",
      summaryPoints: [
        "En aquesta pantalla només s'omplen quantitats.",
        "Si després canvies una quantitat, afegirem o traurem espais d'habitacions.",
        "Les dades detallades venen a la pantalla següent.",
      ],
    },
    grid: {
      eyebrow: "Detall d'habitacions",
      title: "Completa el detall de les teves habitacions en una graella",
      copy:
        "Cada fila és una habitació. Pots copiar una habitació i enganxar les seves dades en una altra per avançar molt més ràpid.",
      backToSetup: "Tornar a quantitat d'habitacions",
      save: "Guardar progrés",
      reload: "Recarregar",
      copyHint: "Habitació copiada",
      copyEmpty: "Encara no has copiat cap habitació",
      roomNumber: "Habitació",
      floor: "Planta",
      sqMeters: "m²",
      numBeds: "Llits",
      bedType: "Tipus de llit",
      view: "Vista",
      features: "Característiques",
      description: "Descripció",
      roomNumberPlaceholder: "201",
      bedTypePlaceholder: "Llit doble",
      viewPlaceholder: "Vista a la mar",
      featuresPlaceholder: "Balcó, sofà llit...",
      descriptionPlaceholder: "Nota opcional",
      copyRow: "Copiar",
      pasteRow: "Enganxar",
      emptyTitle: "Primer crea la llista d'habitacions",
      emptyCopy: "Ves a Quantitat d'habitacions, indica les quantitats i crea la llista abans d'omplir la graella.",
      clipboardCopied: "Habitació copiada. Ja la pots enganxar en una altra fila.",
      clipboardEmpty: "Primer copia una habitació.",
      clipboardPasted: "Les dades s'han enganxat en aquesta fila.",
      saveSuccess: "La graella d'habitacions s'ha guardat correctament.",
      loadError: "No hem pogut carregar les teves habitacions desades.",
      saveError: "No hem pogut guardar la graella d'habitacions ara mateix.",
      validationDuplicate: (roomNumber) => `El número d'habitació ${roomNumber} està repetit.`,
      summaryTitle: "Una manera pràctica d'omplir aquesta graella",
      summaryIntro:
        "Copia una fila completa i enganxa-la a habitacions semblants. Després ajusta només allò que canviï.",
      summaryPoints: [
        "Fes servir Quantitat d'habitacions si necessites canviar el nombre d'habitacions.",
        "Copia des d'habitacions semblants per no escriure el mateix una vegada i una altra.",
        "Pots guardar en qualsevol moment.",
      ],
      summaryTotalLabel: "Habitacions en aquest pas",
      summaryCategoriesLabel: "Categories amb habitacions",
      summarySavedLabel: "Últim desat",
      summaryNotSaved: "Encara sense guardar",
    },
  },
};

export function getRoomsMessages(languageId) {
  return ROOMS_MESSAGES[languageId] || ROOMS_MESSAGES.en;
}
